// Logging in, as Account.dc.html draws it: the line about codes, Set a password and Log out on
// every device, with the board's "Done. Every other device is logged out." beside them. Once a
// password is set, the line about codes says they log in with a code or their password (Adam, 3 Oct).
//
// - Set a password opens a short form. The password goes to the hub's own set-password function,
//   which sets it through Supabase's admin API and sends nothing (never resetPasswordForEmail or
//   reauthenticate). Supabase then ends every session of the login, this one included, so the app
//   logs straight back in with the password just typed and the member stays here; every other
//   device asks them to log in again.
// - Log out on every device goes to log-out-everywhere, which ends every other session and keeps
//   this one. Once it's done, the online tools' working kept on this device for them goes too
//   (lib/auth.js forgetToolWorking), as it does on every other way out.
// - Log out logs this browser out only (lib/auth.js). The board has no plain log out; the hub has
//   had it since stage 1, and Account is where it lives. A view as still open is stopped first, so
//   its stop is in the audit log (stage 5).
// - A login shared with POYT's site (C37, Adam 2 Oct; since migration 82 the Accelerator's old site,
//   which forwards to the hub, no longer counts) can't set a password here: the lede says they log in
//   with a code or that site's password, and sends them to Adam to change it. Log out on every device
//   works for them, with a line under it, and under it only, saying it logs them out of that site too
//   (member review, 11 Oct: a plain Log out sits beside it, which doesn't), and once it's done Account's
//   own line for the shared answer, naming only that site (the review of 23 Oct). Shared comes from
//   hub.my_login_shared(), decided now (account/load.js).
import { useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { forgetToolWorking, logOut } from '../lib/auth.js'
import { useMember } from '../app/member.jsx'
import { stopViewingBeforeLogOut } from '../lib/viewAs.jsx'
import { navigate } from '../lib/router.jsx'
import { COPY, checkPassword, lineFor } from './copy.js'
import { callMember } from './load.js'

export default function LoggingIn({ person, shared, readOnly, onPerson, onShared }) {
  const { me } = useMember()
  const [open, setOpen] = useState(false)
  const [pw, setPw] = useState('')
  const [pwBusy, setPwBusy] = useState(false)
  const [pwError, setPwError] = useState('')
  const [pwMsg, setPwMsg] = useState('')
  const [outBusy, setOutBusy] = useState(false)
  const [outMsg, setOutMsg] = useState('')
  const [outError, setOutError] = useState('')
  const pwRef = useRef(null)
  const openRef = useRef(null)

  function openForm() {
    if (readOnly) return
    setOpen(true)
    setPwMsg('')
    setPwError('')
    setTimeout(() => pwRef.current?.focus(), 0)
  }

  function closeForm() {
    setOpen(false)
    setPw('')
    setPwError('')
    setTimeout(() => openRef.current?.focus(), 0)
  }

  async function savePassword(e) {
    e.preventDefault()
    if (pwBusy || readOnly) return
    setPwError('')
    const problem = checkPassword(pw, person.email)
    if (problem) { setPwError(problem); return }
    setPwBusy(true)
    try {
      const d = await callMember('set-password', { password: pw })
      // every session of the login has just ended, this one included: log in again with it
      const { error } = await supabase.auth.signInWithPassword({ email: person.email, password: pw })
      setPw('')
      setOpen(false)
      // if this device couldn't log straight back in, 1 line that says so (Adam, 3 Oct)
      setPwMsg(error ? COPY.passwordSavedSignInAgain : d.message || COPY.passwordSaved)
      onPerson({ ...person, password_set_at: d.password_set_at || new Date().toISOString() })
      setTimeout(() => openRef.current?.focus(), 0)
    } catch (err) {
      if (err.code === 'shared_login') onShared()
      setPwError(lineFor(err))
    } finally {
      setPwBusy(false)
    }
  }

  async function logOutEverywhere() {
    if (outBusy || readOnly) return
    setOutBusy(true)
    setOutMsg('')
    setOutError('')
    try {
      const d = await callMember('log-out-everywhere', {})
      forgetToolWorking(me.person.id)
      // a shared login's answer in Account's own words, which name only the site that still shares it (the function's
      // own line names both until it's deployed again); any other answer as the function words it
      setOutMsg(d.shared === true ? COPY.loggedOutEverywhereShared : d.message || COPY.loggedOutEverywhere)
    } catch (err) {
      setOutError(lineFor(err))
    } finally {
      setOutBusy(false)
    }
  }

  async function logOutHere() {
    if (readOnly) return
    await stopViewingBeforeLogOut(me)
    await logOut()
    navigate('/', { replace: true })
  }

  return (
    <section id="login" className="ac-card ac-card--login" aria-labelledby="login-h">
      <h2 id="login-h" className="ac-h2">{COPY.loggingIn}</h2>
      <p className="ac-lede">{shared ? COPY.loginLedeShared : person.password_set_at ? COPY.loginLedePassword : COPY.loginLede}</p>

      {open && !shared && (
        <form className="ac-pw" onSubmit={savePassword} noValidate>
          {/* the login's email, for password managers */}
          <input className="sr-only" type="email" name="username" autoComplete="username" value={person.email} readOnly tabIndex={-1} aria-hidden="true" />
          <label htmlFor="ac-pw" className="ac-label">{COPY.newPassword}</label>
          <input id="ac-pw" ref={pwRef} className="ac-input ac-pw-input" type="password" autoComplete="new-password"
            value={pw} onChange={e => { setPw(e.target.value); setPwError('') }} aria-describedby="ac-pw-hint" />
          <span id="ac-pw-hint" className="ac-hint">{COPY.passwordHint}</span>
          {pwError && <p className="ac-msg ac-msg--error" role="alert">{pwError}</p>}
          <div className="ac-row">
            <button type="submit" className="ac-btn ac-btn--ink" disabled={pwBusy} aria-busy={pwBusy || undefined}>{COPY.savePassword}</button>
            <button type="button" className="ac-btn" onClick={closeForm} disabled={pwBusy}>{COPY.cancel}</button>
          </div>
        </form>
      )}

      <div className="ac-row ac-row--login">
        {!shared && !open && (
          <button type="button" ref={openRef} className="ac-btn" onClick={openForm} disabled={readOnly}>
            {person.password_set_at ? COPY.changePassword : COPY.setPassword}
          </button>
        )}
        <button type="button" className="ac-btn" onClick={logOutEverywhere} disabled={readOnly || outBusy} aria-busy={outBusy || undefined}>
          {COPY.logOutEverywhere}
        </button>
        {/* straight under Log out on every device on a phone; on a laptop the line under the row, starting
            under that button, which is the row's first when the login is shared */}
        {shared && !outMsg && <p className="ac-note ac-row-note">{COPY.sharedLogOut}</p>}
        <button type="button" className="ac-btn" onClick={logOutHere} disabled={readOnly}>{COPY.logOut}</button>
        <span className="ac-msg ac-row-msg" role="status">{outMsg}</span>
      </div>
      {outError && <p className="ac-msg ac-msg--error" role="alert">{outError}</p>}
      {pwMsg && <p className="ac-msg" role="status">{pwMsg}</p>}
    </section>
  )
}
